import axios from "axios";
import apiClient from "config/apiClient";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import Notify from "config/notify";
import { API_BASE_URL } from "../../../config";
import { MstItemDetailTireSelectRequest, Tire, TireResponse } from "../types/tire";

/**
 * Handles errors by logging them or sending them to an error tracking service.
 * @param {string} message - The error message.
 * @param {any} error - The error object.
 */
const handleError = (message: string, error: any) => {
  // Replace this with your error handling logic
  console.error(message, error);
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("access_token");
  return {
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Fetches a list of tires based on the provided search parameters.
 *
 * @param {MstItemDetailTireSelectRequest} params - The search parameters for fetching tires.
 * @param {string} [params.item_cd] - The item code of the tire.
 * @param {string} [params.jan_cd] - The JAN code of the tire.
 * @param {string} [params.tire_size] - The size of the tire.
 * @param {string} [params.brand_cd] - The brand code of the tire.
 * @param {string} [params.tire_speed_mark] - The speed mark of the tire.
 * @param {string} [params.tire_label] - The label of the tire.
 * @param {boolean} [params.recommend_flag] - The recommendation flag of the tire.
 * @returns {Promise<any>} A promise that resolves to the response data containing the list of tires.
 */
const fetchTires = async (params?: MstItemDetailTireSelectRequest): Promise<any> => {
  const searchParams: MstItemDetailTireSelectRequest = {
    limit: params?.limit || DEFAULT_LIMIT_ITEMS,
    offset: params?.offset || 1,
  };

  if (params?.item_cd) searchParams.item_cd = params.item_cd;
  if (params?.jan_cd) searchParams.jan_cd = params.jan_cd;
  if (params?.tire_size) searchParams.tire_size = params.tire_size;
  if (params?.brand_cd) searchParams.brand_cd = params.brand_cd;
  if (params?.brand_name) searchParams.brand_name = params.brand_name;
  if (params?.maker_name) searchParams.maker_name = params.maker_name;
  if (params?.tire_speed_mark) searchParams.tire_speed_mark = params.tire_speed_mark;
  if (params?.tire_label) searchParams.tire_label = params.tire_label;
  if (params?.recommend_flag !== undefined) searchParams.recommend_flag = params.recommend_flag;
  const response = await apiClient.post<TireResponse>("/mst/item/detail/tire/select", searchParams);
  return response.data;
};

/**
 * Updates the tire details.
 * @param {Tire} tire - The tire object containing updated details.
 * @returns {Promise<any>} The data from the API response.
 * @throws Will throw an error if the request fails.
 */
const updateTire = async (tire: Tire): Promise<any> => {
  const response = await apiClient.post<TireResponse>("/mst/item/detail/tire/update", {
    item_cd: tire.item_cd,
    mst_item_cashback: {
      cb_tire_price: Number(tire.cb_tire_price),
      cb_wheel_price: Number(tire.cb_wheel_price),
    },
    recommend_flag: Number(tire.recommend_flag),
    status_flag: Number(tire.status_flag),
    remarks: tire.remarks,
    tire_aspect_ratio: Number(tire.tire_aspect_ratio),
    tire_label: tire.tire_label,
    tire_loin_speed_mark: tire.tire_loin_speed_mark,
    tire_rim_diam: tire.tire_rim_diam,
    tire_width: Number(tire.tire_width),
    tire_category: tire.tire_category,
  });

  if (response.status === 200) {
    Notify.setNotification({
      show: true,
      title: "更新が完了しました",
      confirmLabel: "OK",
      actionsPosition: "center",
    });
  }

  return response.data;
};

export { fetchTires, updateTire };
